interface RenderableTestimonial {
  testimonial_text: string
  question_text: string | null
  attribution_line_1: string | null
  attribution_line_2: string | null
  attribution_line_3: string | null
  nps_score: number | null
  avatar_url?: string | null
}

export default class SvgTestimonial {
  svgTemplate: string
  renderableTestimonial: RenderableTestimonial
  svgDoc: Document
  conditionalPrefix = 'IF_'
  placeholders = {
    QUOTE_TEXT: 'testimonial_text',
    QUESTION_TEXT: 'question_text',
    ATTRIBUTION_1: 'attribution_line_1',
    ATTRIBUTION_2: 'attribution_line_2',
    ATTRIBUTION_3: 'attribution_line_3',
    NPS_SCORE: 'nps_score',
    HEADSHOT: 'avatar_url',
  }

  constructor(svgTemplate: string, renderableTestimonial: RenderableTestimonial) {
    this.svgTemplate = svgTemplate
    this.renderableTestimonial = renderableTestimonial
    this.svgDoc = new DOMParser().parseFromString(this.svgTemplate, 'image/svg+xml')
  }

  markup() {
    this.processConditionalElements()
    this.replaceAllPlaceholders()
    return this.svgDoc.documentElement.outerHTML
  }

  processConditionalElements() {
    // Get all elements with IDs that start with the conditional prefix
    const elements = Array.from(
      this.svgDoc.querySelectorAll('[id^="' + this.conditionalPrefix + '"]'),
    )

    elements.forEach((element) => {
      const id = element.id
      // Extract the field name from the ID by removing the prefix
      const field = id.substring(this.conditionalPrefix.length)

      // Map to the actual data field if it's in our placeholders mapping
      const dataField = Object.entries(this.placeholders).find(
        ([placeholderId]) => placeholderId === field,
      )?.[1]

      // Only process if we found a matching placeholder field
      if (dataField) {
        // Get the value from the testimonial data
        const value = this.renderableTestimonial[dataField]

        // If the content is empty, remove the element
        if (this.isEmptyContent(value)) {
          element.parentNode?.removeChild(element)
        }
      } else {
        // If we can't find a matching field, remove the element since we can't evaluate it
        element.parentNode?.removeChild(element)
      }
    })
  }

  replaceAllPlaceholders() {
    Object.entries(this.placeholders).forEach(([placeholderId, contentKey]) => {
      const value = this.renderableTestimonial[contentKey]
      // Convert number values to string before passing to replacePlaceholder
      const content = typeof value === 'number' ? String(value) : value
      this.replacePlaceholder(placeholderId, content)
    })
  }

  replacePlaceholder(placeholderId: string, content: string | null) {
    const element = this.svgDoc.getElementById(placeholderId)
    if (!element) {
      return
    }

    // If content is empty (null, empty string, or only whitespace), remove the element entirely
    if (this.isEmptyContent(content)) {
      element.parentNode?.removeChild(element)
      return
    }

    // Special case for HEADSHOT placeholder
    if (placeholderId === 'HEADSHOT' && element.tagName.toLowerCase() === 'path') {
      this.replaceHeadshotPath(element, content)
      return
    }

    // Check if the element matches the structure expected for a complex replacement
    if (this.isComplexStructure(element)) {
      this.replaceGroupWithForeignObject(placeholderId, content)
    } else {
      this.replaceText(placeholderId, content)
    }
  }

  /**
   * Determines if content should be considered "empty" for rendering purposes
   * Content is considered empty if it is null, an empty string, or contains only whitespace
   */
  private isEmptyContent(content: string | null): boolean {
    return (
      content === null || content === '' || (typeof content === 'string' && content.trim() === '')
    )
  }

  /**
   *
   * Determines if the element has the complex structure required for foreignObject replacement
   * (contains both a text element and a rect/path for positioning)
   */
  private isComplexStructure(element: Element): boolean {
    const hasTextElement = !!element.querySelector('text')
    const hasRectOrPath = !!element.querySelector('rect') || !!element.querySelector('path')

    return hasTextElement && hasRectOrPath
  }

  /** Figma replaces shapes with paths...
   * Look for a rect in the group. If it is not found, then look for a path
   * and then make a temporary rect from it's bounding area. */
  private getRectFromGroup(group) {
    let rect = group.querySelector('rect')

    if (!rect) {
      const path = group.querySelector('path')

      if (path) {
        // Create a clone of the SVG for measurement purposes only
        const tempSvg = this.svgDoc.documentElement.cloneNode(true) as SVGSVGElement

        // Append to DOM temporarily
        document.body.appendChild(tempSvg)

        // Find the equivalent path in the temp SVG
        // We need to find the same path in the cloned document
        const tempGroup = tempSvg.getElementById(group.id)
        const tempPath = tempGroup ? tempGroup.querySelector('path') : null

        if (tempPath) {
          // Get dimensions from the temporary path
          const pathBox = tempPath.getBBox()

          // Create rect with the correct dimensions
          rect = this.svgDoc.createElementNS('http://www.w3.org/2000/svg', 'rect')
          rect.setAttribute('x', pathBox.x.toString())
          rect.setAttribute('y', pathBox.y.toString())
          rect.setAttribute('width', pathBox.width.toString())
          rect.setAttribute('height', pathBox.height.toString())
        }

        // Remove the temp SVG
        document.body.removeChild(tempSvg)
      }
    }

    return rect
  }

  private replaceGroupWithForeignObject(groupId: string, content: string | null) {
    const group = this.svgDoc.getElementById(groupId)
    if (!group) {
      return
    }

    // Get positioning from the rect
    const rect = this.getRectFromGroup(group)

    if (!rect) {
      throw new Error(`No rectangle found in ${groupId} placeholder`)
    }

    // Get styles from the text
    const textElement = group.querySelector('text')
    if (!textElement) {
      throw new Error(`No text element found in ${groupId} placeholder`)
    }

    const foreignObject = this.svgDoc.createElementNS('http://www.w3.org/2000/svg', 'foreignObject')

    // Copy positioning and dimensions from the rect
    const rectWidth = parseFloat(rect.getAttribute('width') || '0')
    const rectHeight = parseFloat(rect.getAttribute('height') || '0')

    foreignObject.setAttribute('x', rect.getAttribute('x') || '0')
    foreignObject.setAttribute('y', rect.getAttribute('y') || '0')
    foreignObject.setAttribute('width', rectWidth.toString())
    foreignObject.setAttribute('height', rectHeight.toString())
    foreignObject.setAttribute('transform', rect.getAttribute('transform') || '')

    // Create the div with text styles from the text element
    const div = this.svgDoc.createElementNS('http://www.w3.org/1999/xhtml', 'div')
    div.style.width = '100%'
    div.style.height = '100%'
    div.style.overflow = 'hidden'

    // Get initial text styles
    const initialFontSize = parseFloat(textElement.getAttribute('font-size') || '16')
    const styles = {
      color: textElement.getAttribute('fill') || 'black',
      fontFamily: textElement.getAttribute('font-family') || 'Arial',
      fontSize: `${initialFontSize}px`,
      fontStyle: textElement.getAttribute('font-style') || 'normal',
      fontWeight: textElement.getAttribute('font-weight') || '400',
      lineHeight: '1.2', // Set a default line height ratio
    }

    Object.assign(div.style, styles)
    div.innerHTML = content

    // Create a temporary container to test sizing
    const tempContainer = document.createElement('div')
    tempContainer.style.position = 'absolute'
    tempContainer.style.visibility = 'hidden'
    tempContainer.style.width = `${rectWidth}px` // Set same width as target

    // Create test div with same content and initial styles
    const testDiv = div.cloneNode(true)
    tempContainer.appendChild(testDiv)
    document.body.appendChild(tempContainer)

    // Start with the initial font size and decrease until content height fits
    let currentFontSize = initialFontSize
    while (currentFontSize > 1 && tempContainer.offsetHeight > rectHeight) {
      currentFontSize -= 1
      testDiv.style.fontSize = `${currentFontSize}px`
    }

    // Clean up temporary element
    document.body.removeChild(tempContainer)

    // Apply the final font size to the actual div
    div.style.fontSize = `${currentFontSize}px`

    foreignObject.appendChild(div)
    group.parentNode.replaceChild(foreignObject, group)

    return this.svgDoc
  }

  private replaceText(targetId: string, sourceText: string | null) {
    const targetElement = this.svgDoc.getElementById(targetId)
    if (!targetElement) {
      return
    }

    const tspan = targetElement.querySelector('tspan')
    if (tspan) {
      tspan.textContent = sourceText
    } else {
      // If there's no tspan, try to set the text content directly
      targetElement.textContent = sourceText
    }
  }

  private replaceHeadshotPath(pathElement: Element, avatarUrl: string | null) {
    if (pathElement.tagName.toLowerCase() !== 'path') {
      return
    }

    // If avatarUrl is null or undefined, we should remove the element
    if (avatarUrl === null || avatarUrl === undefined) {
      pathElement.parentNode?.removeChild(pathElement)
      return
    }

    // Generate a unique ID for our pattern using a random string
    const patternId = `headshot-pattern-${Math.random().toString(36).substring(2, 15)}`

    // Create pattern element with objectBoundingBox units for scaling to path bounds
    const pattern = this.svgDoc.createElementNS('http://www.w3.org/2000/svg', 'pattern')
    pattern.setAttribute('id', patternId)
    pattern.setAttribute('patternContentUnits', 'objectBoundingBox')
    pattern.setAttribute('width', '1')
    pattern.setAttribute('height', '1')

    // Create image element inside pattern
    const image = this.svgDoc.createElementNS('http://www.w3.org/2000/svg', 'image')
    image.setAttribute('href', avatarUrl)
    image.setAttribute('x', '0')
    image.setAttribute('y', '0')
    image.setAttribute('width', '1')
    image.setAttribute('height', '1')
    image.setAttribute('preserveAspectRatio', 'xMidYMid slice')

    // Add image to pattern
    pattern.appendChild(image)

    // Get or create defs element to hold our pattern
    let defs = this.svgDoc.querySelector('defs')
    if (!defs) {
      defs = this.svgDoc.createElementNS('http://www.w3.org/2000/svg', 'defs')
      this.svgDoc.documentElement.insertBefore(defs, this.svgDoc.documentElement.firstChild)
    }

    // Add pattern to defs
    defs.appendChild(pattern)

    // Update the path's fill to reference our new pattern
    pathElement.setAttribute('fill', `url(#${patternId})`)
  }
}
